import DOMPurify from 'dompurify';
import type { EmailAddress, InheritedAttachment } from '@/types';
import { SIGNATURE_WRAPPER_CLASS } from '@/types/emailSignature';

import { formatDate as coreFormatDate, formatDateTime as coreFormatDateTime, formatTime as coreFormatTime } from '@/composables/useFormat'
import { QUOTE_CLASS } from './emailQuote'
import { threadSubjectKey } from './emailSubject';
export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    WHOLE_DOCUMENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    FORCE_BODY: false,
    FORBID_TAGS: ['link', 'meta', 'script', 'form', 'input', 'textarea', 'select', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'http-equiv'],
    ADD_TAGS: ['style', 'head', 'html', 'body'],
    ADD_ATTR: ['style', 'class', 'id', 'width', 'height', 'align', 'valign', 'bgcolor', 'color', 'face', 'size', 'border', 'cellpadding', 'cellspacing', 'target', 'rel', 'type', 'charset'],
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    KEEP_CONTENT: true,
  });
}

// Ancho máximo que un correo aguanta sin recortarse en Outlook (≈ 600 px = 450 pt).
const MAX_EMAIL_WIDTH_PX = 600;
const TABLE_PART_TAGS = new Set(['TABLE', 'TR', 'TD', 'TH']);
const REMOVED_STYLE_PROPS = new Set(['position', 'float', 'top', 'left', 'right', 'bottom']);

/** Ancho en px de un valor CSS o de atributo (`720`, `720px`, `540pt`); null si es relativo (%, auto). */
function widthInPx(value: string): number | null {
  const match = value.trim().match(/^(\d+(?:\.\d+)?)(px|pt)?$/i);
  if (!match) return null;
  const n = parseFloat(match[1]);
  return match[2]?.toLowerCase() === 'pt' ? n * 4 / 3 : n;
}

function isTooWide(value: string): boolean {
  const px = widthInPx(value);
  return px !== null && px > MAX_EMAIL_WIDTH_PX;
}

/**
 * Deja solo las declaraciones de estilo que Outlook de escritorio (motor Word)
 * pinta en el flujo normal. Devuelve las declaraciones como pares.
 */
function cleanStyleDeclarations(style: string, tagName: string): Array<[string, string]> {
  const kept: Array<[string, string]> = [];
  // `;` dentro de url(data:...;base64,...) no separa declaraciones
  for (const declaration of style.split(/;(?![^(]*\))/)) {
    const colon = declaration.indexOf(':');
    if (colon < 0) continue;
    const prop = declaration.slice(0, colon).trim().toLowerCase();
    const value = declaration.slice(colon + 1).trim();
    if (!prop || !value) continue;
    if (prop.startsWith('mso-') || REMOVED_STYLE_PROPS.has(prop)) continue;
    if (prop.startsWith('margin') && /(^|\s)-\.?\d/.test(value)) continue;
    if (prop === 'display' && /flex|grid/i.test(value)) continue;
    if (TABLE_PART_TAGS.has(tagName) && (prop === 'height' || prop === 'min-height')) continue;
    kept.push([prop, value]);
  }
  return kept;
}

function outlookSafeElement(node: Element): void {
  const tagName = node.tagName;
  const style = node.getAttribute('style') ?? '';

  // Filas y bloques ocultos de Word/Excel: Outlook a veces los muestra o los superpone
  if (/display\s*:\s*none/i.test(style)) {
    node.remove();
    return;
  }

  const declarations = cleanStyleDeclarations(style, tagName);

  if (TABLE_PART_TAGS.has(tagName)) node.removeAttribute('height');

  if (tagName === 'TABLE') {
    // align=left|right vuelve la tabla flotante en Word: el contenido de abajo se le monta encima
    node.removeAttribute('align');
    const attrWidth = node.getAttribute('width');
    const styleWidth = declarations.find(([p]) => p === 'width');
    if ((attrWidth && isTooWide(attrWidth)) || (styleWidth && isTooWide(styleWidth[1]))) {
      node.removeAttribute('width');
      if (styleWidth) styleWidth[1] = '100%';
      else declarations.push(['width', '100%']);
    }
    if (!declarations.some(([p]) => p === 'border-collapse')) declarations.push(['border-collapse', 'collapse']);
  } else if (tagName === 'TD' || tagName === 'TH') {
    // Una celda más ancha que el correo sigue recortando aunque la tabla quede al 100%
    const attrWidth = node.getAttribute('width');
    if (attrWidth && isTooWide(attrWidth)) node.removeAttribute('width');
    const i = declarations.findIndex(([p, v]) => p === 'width' && isTooWide(v));
    if (i >= 0) declarations.splice(i, 1);
  }

  if (declarations.length) node.setAttribute('style', declarations.map(([p, v]) => `${p}:${v}`).join(';'));
  else node.removeAttribute('style');
}

let outlookPurifier: ReturnType<typeof DOMPurify> | null = null;

/**
 * Sanitiza el HTML que sale de Kila hacia otros buzones (lo pegado en el editor
 * y el cuerpo al enviar) para que Outlook de escritorio no lo superponga ni lo
 * recorte. Lo pegado desde Outlook/Word/Excel trae tablas flotantes, estilos
 * posicionados, anchos fijos en pt y filas ocultas que el motor Word respeta.
 *
 * Usa una instancia propia de DOMPurify para que su hook no altere `sanitizeHtml`.
 */
export function sanitizeEmailHtml(html: string): string {
  if (!outlookPurifier) {
    outlookPurifier = DOMPurify(window);
    outlookPurifier.addHook('afterSanitizeAttributes', node => {
      if (node.nodeType === Node.ELEMENT_NODE) outlookSafeElement(node as Element);
    });
  }
  return outlookPurifier.sanitize(html, {
    FORBID_TAGS: ['style', 'link', 'meta', 'xml', 'script', 'form', 'input', 'textarea', 'select', 'button', 'col', 'colgroup'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'http-equiv'],
    ADD_ATTR: ['style', 'class', 'id', 'width', 'height', 'align', 'valign', 'bgcolor', 'color', 'face', 'size', 'border', 'cellpadding', 'cellspacing', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    KEEP_CONTENT: true,
  });
}

/**
 * Garantiza una línea editable al inicio del editor del compositor.
 *
 * Sin ella el primer hijo del contenteditable es el div de la firma (o el <br>
 * de la cita), y el navegador no ofrece punto de inserción encima: con firmas
 * que traen una línea superior el usuario no podía escribir en la parte alta
 * del correo.
 *
 * Solo antepone cuando hace falta, para no acumular líneas en blanco cada vez
 * que se remonta el editor (reabrir un borrador, aplicar una plantilla).
 */
export function ensureLeadingBlankLine(container: HTMLElement): void {
  const first = container.firstChild;
  const firstElement = first?.nodeType === Node.ELEMENT_NODE ? (first as HTMLElement) : null;

  const needsBlankLine = !first
    || !!firstElement && (firstElement.tagName === 'BR' || firstElement.classList.contains(SIGNATURE_WRAPPER_CLASS) || firstElement.classList.contains(QUOTE_CLASS));

  if (!needsBlankLine) return;

  // <div> y no <p>: es el bloque que produce execCommand en Chrome, así el
  // editor mantiene una sola convención de párrafo.
  const line = document.createElement('div');
  line.appendChild(document.createElement('br'));
  container.insertBefore(line, first);
}

export function getEmailInitials(email: EmailAddress): string {
  if (email.name) {
    const parts = email.name.trim().split(' ');
    if (parts.length >= 2 && parts[0] && parts[parts.length - 1]) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    if (parts[0]) {
      return parts[0].substring(0, 2).toUpperCase();
    }
  }
  
  return email.email.substring(0, 2).toUpperCase();
}

export function formatEmailAddress(email: EmailAddress): string {
  if (email.name) {
    return `${email.name} <${email.email}>`;
  }
  return email.email;
}

export function formatEmailAddressList(emails: EmailAddress[]): string {
  return emails.map(formatEmailAddress).join(', ');
}

export function formatRelativeDate(dateStr: string): string {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `hace ${diffMins} min`;
    if (diffHours < 24) return `hace ${diffHours}h`;
    if (diffDays < 7) return `hace ${diffDays}d`;

    return coreFormatDate(date);
  } catch {
    return dateStr;
  }
}

export function formatFullDate(dateStr: string): string {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return coreFormatDateTime(date);
  } catch {
    return dateStr;
  }
}

export function formatEmailDate(dateStr: string): string {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return coreFormatTime(date);
    }

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Ayer';
    }

    return coreFormatDate(date);
  } catch {
    return dateStr;
  }
}

/**
 * Último correo de la conversación que NO salió del buzón conectado.
 *
 * La suscripción al proveedor cubre el buzón entero (`me/messages`), así que
 * los enviados llegan a Kila junto con los recibidos y el backend los marca
 * pero nadie los separaba: un correo propio abría su propia fila en la bandeja
 * y mandaba en la vista previa del hilo. La comparación es contra el buzón de
 * QUIEN mira, no contra la bandera del documento: hay un solo documento por
 * compañía, así que el enviado de una persona es el recibido de su colega.
 *
 * Regla de producto: un correo propio en el que voy en Para o CC sí es de la
 * bandeja (p. ej. reenviármelo desde Outlook). Outlook deja dos copias con el
 * mismo internetMessageId y Kila guarda una sola, la de Enviados, así que sin
 * esta excepción el autoenvío nunca llegaba a la bandeja.
 *
 * `null` = el hilo solo tiene correos míos y no pertenece a la bandeja (queda
 * en Enviados). La conversación se espera ordenada de más reciente a más
 * antigua, como la arma el store de la bandeja.
 */
export function latestIncomingEmail<
  T extends { from: EmailAddress; to?: EmailAddress[]; cc?: EmailAddress[] },
>(
  conversation: T[],
  mailboxEmail?: string | null,
): T | null {
  const mailbox = mailboxEmail?.trim().toLowerCase();
  if (!mailbox) return conversation[0] || null;
  const isMailbox = (address?: EmailAddress) => address?.email?.trim().toLowerCase() === mailbox;
  return conversation.find(email =>
    !isMailbox(email.from) || (email.to || []).some(isMailbox) || (email.cc || []).some(isMailbox)
  ) || null;
}

/**
 * Remitente, Para y CC de un correo que no son del dominio del buzón, en
 * minúsculas. Los compañeros de la compañía van en casi todo, así que no
 * sirven para decir que dos correos son de la misma conversación.
 */
export function externalParticipants(
  email: {
    from?: EmailAddress; to?: EmailAddress[]; cc?: EmailAddress[];
    // Forma de Firestore (`EmailComplete`, resultados de búsqueda)
    fromAddress?: string; toAddresses?: string[]; ccAddresses?: string[];
  },
  mailboxEmail?: string | null,
): string[] {
  const domain = mailboxEmail?.trim().toLowerCase().split('@')[1];
  return [
    email.from?.email, ...(email.to || []).map(a => a?.email), ...(email.cc || []).map(a => a?.email),
    email.fromAddress, ...(email.toAddresses || []), ...(email.ccAddresses || []),
  ]
    .map(address => address?.trim().toLowerCase() || '')
    .filter(address => address && (!domain || !address.endsWith(`@${domain}`)));
}

/** Un hilo: sus mensajes del más reciente al más antiguo. */
export interface ConversationGroup<T> {
  /** conversationId (o id, si no tiene) del mensaje más antiguo del hilo; estable al llegar mensajes nuevos */
  key: string;
  /** El mensaje más reciente del hilo (= emails[0]) */
  latestEmail: T;
  emails: T[];
  count: number;
}

/** Instante de un mensaje para ordenar el hilo y los hilos entre sí; inválido o ausente cuenta como 0. */
function messageTime(email: { receivedDateTime?: string; receivedAt?: unknown }): number {
  if (typeof email.receivedDateTime === 'string' && email.receivedDateTime) {
    const fromString = new Date(email.receivedDateTime).getTime();
    if (!Number.isNaN(fromString)) return fromString;
  }

  const receivedAt = email.receivedAt as any;
  if (receivedAt instanceof Date) return receivedAt.getTime();
  if (receivedAt && typeof receivedAt.toMillis === 'function') return receivedAt.toMillis();
  if (receivedAt && typeof receivedAt.toDate === 'function') {
    const fromTimestamp = receivedAt.toDate().getTime();
    return Number.isNaN(fromTimestamp) ? 0 : fromTimestamp;
  }
  if (typeof receivedAt === 'string' || typeof receivedAt === 'number') {
    const fromValue = new Date(receivedAt).getTime();
    return Number.isNaN(fromValue) ? 0 : fromValue;
  }

  return 0;
}

/**
 * Agrupa correos en hilos de forma genérica: sirve tanto para `EmailComplete`
 * (tiene `receivedAt`) como para `Email` (tiene `receivedDateTime`).
 *
 * Dos correos van al mismo hilo si comparten `conversationId` o la misma
 * `threadSubjectKey` no vacía, y la unión es transitiva. En Outlook el
 * `conversationId` es propio de cada buzón y Kila guarda un solo documento por
 * mensaje, así que un mismo hilo puede llegar con varios `conversationId`. Un
 * correo sin `conversationId` ni asunto útil queda solo, con `key = id`.
 *
 * Con `participants`, el asunto solo une correos que comparten algún
 * participante: la bandeja general mezcla todos los clientes y un asunto
 * genérico ("Factura") no puede juntar conversaciones de personas distintas.
 * El Buzón de un embarque no lo necesita: ya está acotado a una operación.
 *
 * No muta el array de entrada; los correos del resultado son las mismas
 * referencias de la entrada, así marcar leído o gestión se ve a la vez en la
 * lista y en el detalle.
 */
export function groupEmailsByConversation<
  T extends { id: string; conversationId?: string; subject?: string; receivedDateTime?: string; receivedAt?: unknown }
>(emails: T[], options: { participants?: (email: T) => string[] } = {}): ConversationGroup<T>[] {
  // Unión-búsqueda por índice
  const parent: number[] = emails.map((_, i) => i);
  const find = (i: number): number => {
    let root = i;
    while (parent[root] !== root) root = parent[root];
    while (parent[i] !== root) {
      const next = parent[i];
      parent[i] = root;
      i = next;
    }
    return root;
  };
  const union = (a: number, b: number) => {
    parent[find(b)] = find(a);
  };

  const firstByLink = new Map<string, number>();
  // Por asunto, cuando hay `participants`: grupos con las direcciones que ya juntan
  const peopleBySubject = new Map<string, Array<{ first: number; people: Set<string> }>>();
  emails.forEach((email, i) => {
    const links: string[] = [];
    if (email.conversationId) links.push(`c:${email.conversationId}`);
    const subjectKey = threadSubjectKey(email.subject);
    if (subjectKey && !options.participants) links.push(`s:${subjectKey}`);

    for (const link of links) {
      const first = firstByLink.get(link);
      if (first === undefined) firstByLink.set(link, i);
      else union(first, i);
    }

    if (!subjectKey || !options.participants) return;
    const people = new Set(options.participants(email));
    const clusters = peopleBySubject.get(subjectKey) || [];
    const shared = clusters.filter(c => [...people].some(p => c.people.has(p)));
    const merged = { first: shared[0]?.first ?? i, people };
    for (const cluster of shared) {
      union(cluster.first, i);
      cluster.people.forEach(p => merged.people.add(p));
    }
    peopleBySubject.set(subjectKey, [...clusters.filter(c => !shared.includes(c)), merged]);
  });

  const groups = new Map<number, T[]>();
  emails.forEach((email, i) => {
    const root = find(i);
    const group = groups.get(root);
    if (group) group.push(email);
    else groups.set(root, [email]);
  });

  const conversations: ConversationGroup<T>[] = [];
  groups.forEach(groupEmails => {
    const sorted = [...groupEmails].sort((a, b) => messageTime(b) - messageTime(a));
    // El más antiguo aporta la key; con empate de fecha gana el id menor
    const oldest = groupEmails.reduce((min, email) => {
      const diff = messageTime(email) - messageTime(min);
      return diff < 0 || (diff === 0 && email.id < min.id) ? email : min;
    });
    conversations.push({
      key: oldest.conversationId || oldest.id,
      latestEmail: sorted[0],
      emails: sorted,
      count: sorted.length,
    });
  });

  return conversations.sort((a, b) => messageTime(b.latestEmail) - messageTime(a.latestEmail));
}

/** Lo mínimo de un doc de `emails` para reconocer el correo que yo acabo de enviar. */
export interface SentEmailCandidate {
  id: string;
  fromAddress?: string | null;
  sentAt?: unknown;
  receivedAt?: unknown;
}

/** Holgura por reloj del navegador vs. servidor al comparar con el inicio del envío. */
export const OWN_SENT_EMAIL_CLOCK_MARGIN_MS = 5 * 60 * 1000;

/**
 * El correo que acabo de enviar desde Kila, entre los candidatos leídos de Firestore:
 * remitente = mi buzón y fecha >= inicio del envío − holgura. Devuelve el más reciente
 * o null. Sin buzón no se puede saber cuál es el mío → null.
 */
export function pickOwnSentEmail<T extends SentEmailCandidate>(
  candidates: T[],
  mailboxEmail: string | null | undefined,
  sentAfter: Date,
  excludeId?: string,
): T | null {
  const mailbox = mailboxEmail?.trim().toLowerCase();
  if (!mailbox) return null;

  const minTime = sentAfter.getTime() - OWN_SENT_EMAIL_CLOCK_MARGIN_MS;
  let best: T | null = null;
  let bestTime = -Infinity;

  for (const c of candidates) {
    if (c.id === excludeId) continue;
    if ((c.fromAddress || '').trim().toLowerCase() !== mailbox) continue;
    const t = messageTime({ receivedAt: c.sentAt }) || messageTime({ receivedAt: c.receivedAt });
    if (t < minTime) continue;
    if (t > bestTime) {
      best = c;
      bestTime = t;
    }
  }

  return best;
}

export function extractEmailDomain(email: string): string {
  const parts = email.split('@');
  return parts.length > 1 ? parts[1] : '';
}

// Caracteres invisibles que viajan pegados a una dirección al copiarla de un
// chat o de otro correo (espacio de ancho cero, marca de orden de bytes...).
const INVISIBLE_CHARS = /[\u200B-\u200D\u2060\uFEFF]/g;
// Basura que suele quedar en los bordes: comillas, paréntesis y, al final,
// signos de puntuación (`juan@dominio.com:` o `juan@dominio.com.` son los casos
// que más reportan los usuarios). Al inicio no se quitan puntos ni signos: eso
// ya sería cambiar la dirección en vez de limpiarla.
const LEADING_JUNK = /^[\s"'“”‘’<>()[\]{}]+/;
const TRAILING_JUNK = /[\s"'“”‘’<>()[\]{},;:.\/\\|!?*]+$/;

/** Quita invisibles y basura de los bordes de una dirección; no toca el interior. */
export function cleanEmailToken(raw: string): string {
  return raw
    .replace(INVISIBLE_CHARS, '')
    .replace(/\u00A0/g, ' ')
    .replace(LEADING_JUNK, '')
    .replace(TRAILING_JUNK, '')
    .trim();
}

// Parte local: átomos de letras, números y . _ % + - separados por un solo punto
// (sin punto al inicio, al final ni seguidos). Dominio: etiquetas de letras,
// números y guiones (sin guion en los bordes) y un TLD de solo letras, mínimo 2.
const EMAIL_REGEX = /^[A-Za-z0-9_%+-]+(?:\.[A-Za-z0-9_%+-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;

/** Validación estricta: la dirección debe venir ya limpia (ver cleanEmailToken). */
export function isValidEmail(email: string): boolean {
  return email.length <= 254 && EMAIL_REGEX.test(email);
}

/**
 * Separa una lista de destinatarios por `,` `;` y saltos de línea que NO estén
 * dentro de comillas ni de `<>` (`"Pérez, Juan" <jperez@x.com>` queda entero).
 * Varias direcciones sueltas separadas solo por espacios también se separan.
 */
export function splitRecipientList(value: string): string[] {
  const parts: string[] = [];
  let current = '';
  let inQuotes = false;
  let inAngle = false;

  for (const ch of value) {
    if (ch === '"') inQuotes = !inQuotes;
    else if (ch === '<' && !inQuotes) inAngle = true;
    else if (ch === '>' && !inQuotes) inAngle = false;

    if ((ch === ',' || ch === ';' || ch === '\n' || ch === '\r') && !inQuotes && !inAngle) {
      if (current.trim()) parts.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  if (current.trim()) parts.push(current.trim());

  return parts.flatMap(part => {
    if (part.includes('<') || part.includes('"')) return [part];
    const tokens = part.split(/\s+/).filter(Boolean);
    return tokens.length > 1 && tokens.every(t => isValidEmail(cleanEmailToken(t))) ? tokens : [part];
  });
}

/** `Nombre <mail>`, `"Nombre" <mail>`, `<mail>` o `mail`; null si no hay dirección válida. */
export function parseSingleAddress(raw: string): EmailAddress | null {
  const value = raw.trim();
  if (!value) return null;

  const match = value.match(/^(.*?)\s*<([^<>]+)>\s*$/);
  const email = cleanEmailToken(match ? match[2] : value);
  if (!isValidEmail(email)) return null;

  const name = match ? match[1].trim().replace(/^["']|["']$/g, '').trim() : '';
  return name ? { name, email } : { email };
}

/**
 * Devuelve las direcciones válidas y, aparte, los fragmentos que no lo son:
 * antes se descartaban en silencio y el usuario creía que había agregado a
 * todos los que pegó.
 */
export function parseRecipientInput(input: string): { valid: EmailAddress[]; invalid: string[] } {
  const valid: EmailAddress[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();

  for (const part of splitRecipientList(input)) {
    const parsed = parseSingleAddress(part);
    if (!parsed) {
      invalid.push(part);
      continue;
    }
    const key = parsed.email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    valid.push(parsed);
  }

  return { valid, invalid };
}

export function parseEmailAddresses(input: string): EmailAddress[] {
  return parseRecipientInput(input).valid;
}

interface InlineAttachment {
  contentId?: string;
  filename?: string;
  name?: string;
  downloadUrl?: string;
  url?: string;
  isInline?: boolean;
}

/**
 * Adjuntos del correo original que el proveedor va a arrastrar al reenviarlo.
 *
 * El compositor los lista para que el usuario vea que van: como nunca pasan por
 * el navegador, antes la ventana se abría vacía y en Grupo SKY los volvían a
 * subir a mano, con lo que el correo salía con los archivos duplicados.
 *
 * Acepta las dos formas del original porque el compositor lo lee de Firestore
 * (`filename`) con respaldo en el correo que devuelve Graph (`name`).
 */
export function inheritedForwardAttachments(original: any): InheritedAttachment[] {
  const attachments = original?.attachments;
  if (!Array.isArray(attachments)) return [];

  return attachments
    .filter((att: any) => !att?.isInline)
    .map((att: any) => ({
      id: att.id || '',
      name: att.filename || att.name || '',
      size: att.size || 0,
    }));
}

export function normalizeContentId(value: string): string {
  const trimmed = value.trim().replace(/^<|>$/g, '');
  try {
    return decodeURIComponent(trimmed).toLowerCase();
  } catch {
    return trimmed.toLowerCase();
  }
}

/** Content-ID (normalizados) que el HTML referencia con `src="cid:..."`. */
export function referencedContentIds(html: string): Set<string> {
  const ids = new Set<string>();
  const re = /\ssrc\s*=\s*["']\s*cid:([^"']+)["']/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(html))) {
    ids.add(normalizeContentId(match[1]));
  }
  return ids;
}

/**
 * Adjuntos que el usuario ve en la lista del correo: sin las imágenes que ya
 * van pintadas dentro del cuerpo (logos y redes de la firma).
 *
 * Se oculta lo que el HTML referencia con `cid:` aunque el proveedor no lo
 * marque inline (Gmail a veces manda la firma como `attachment` con
 * Content-ID), y las imágenes inline aún sin contentId (antes del
 * enrich-attachments, o en la lista, que no trae el cuerpo). Un inline que no
 * es imagen (un PDF de Apple Mail) sigue visible: es un documento real.
 */
export function visibleAttachments<T extends { contentId?: string; isInline?: boolean; contentType?: string }>(
  attachments: T[] | undefined | null,
  html: string | undefined | null
): T[] {
  if (!Array.isArray(attachments)) return [];
  const referenced = referencedContentIds(html || '');
  return attachments.filter(att => {
    if (att.contentId && referenced.has(normalizeContentId(att.contentId))) return false;
    return !(att.isInline && (att.contentType || '').toLowerCase().startsWith('image/'));
  });
}

/**
 * Si el correo (documento crudo de Firestore) trae adjuntos que el usuario ve,
 * para el clip de la bandeja: uno que solo trae la firma no lo lleva.
 * Sin arreglo de adjuntos (documento podado) se confía en la bandera guardada.
 */
export function hasVisibleAttachments(doc: { attachments?: unknown; bodyHtml?: string; hasAttachments?: boolean }): boolean {
  if (!Array.isArray(doc.attachments) || doc.attachments.length === 0) return !!doc.hasAttachments;
  return visibleAttachments(doc.attachments, doc.bodyHtml).length > 0;
}

/**
 * Reemplaza los src="cid:..." del cuerpo por la URL del adjunto correspondiente.
 *
 * El unico vinculo confiable entre el HTML y un adjunto es el contentId: los
 * nombres casi siempre son "image.png" y el orden en que el proveedor devuelve
 * los adjuntos no coincide con el orden en que aparecen en el cuerpo. Cuando no
 * hay contentId se omite la imagen: adivinar termina mostrando la firma de una
 * persona bajo el mensaje de otra.
 */
export function resolveCidImages(html: string, attachments: InlineAttachment[]): string {
  const urlByContentId = new Map<string, string>();
  for (const att of attachments || []) {
    const url = att.downloadUrl || att.url;
    if (!att.contentId || !url) continue;
    urlByContentId.set(normalizeContentId(att.contentId), url);
  }

  return html.replace(/<img\b[^>]*>/gi, tag => {
    const srcAttr = tag.match(/\ssrc\s*=\s*["']([^"']*)["']/i);
    const rawSrc = srcAttr?.[1];
    if (!srcAttr || !rawSrc) return tag;

    const rawCid = rawSrc.match(/^\s*cid:(.+)$/i)?.[1];
    if (!rawCid) return tag;

    const url = urlByContentId.get(normalizeContentId(rawCid));
    return url ? tag.replace(srcAttr[0], () => ` src="${url}"`) : '';
  });
}

/**
 * Solo para el editor: cid: -> URL de Storage. Las que no resuelven quedan
 * intactas, a diferencia de `resolveCidImages` (para el visor de solo
 * lectura), que las quita: en el editor el usuario tiene que poder ver que la
 * imagen sigue ahí aunque no haya adjunto que la resuelva.
 */
export function cidImagesToUrls(
  html: string,
  attachments: InlineAttachment[]
): { html: string; urlToCid: Record<string, string> } {
  const urlByContentId = new Map<string, string>();
  for (const att of attachments || []) {
    const url = att.downloadUrl || att.url;
    if (!att.contentId || !url) continue;
    urlByContentId.set(normalizeContentId(att.contentId), url);
  }

  const urlToCid: Record<string, string> = {};

  const resultHtml = html.replace(/<img\b[^>]*>/gi, tag => {
    const srcAttr = tag.match(/\ssrc\s*=\s*["']([^"']*)["']/i);
    const rawSrc = srcAttr?.[1];
    if (!srcAttr || !rawSrc) return tag;

    const rawCid = rawSrc.match(/^\s*cid:(.+)$/i)?.[1];
    if (!rawCid) return tag;

    const url = urlByContentId.get(normalizeContentId(rawCid));
    if (!url) return tag;

    urlToCid[url] = rawCid;
    const escapedUrl = url.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    return tag.replace(srcAttr[0], () => ` src="${escapedUrl}"`);
  });

  return { html: resultHtml, urlToCid };
}

/** Justo antes de enviar: URL de Storage -> cid: */
export function urlsToCidImages(html: string, urlToCid: Record<string, string>): string {
  if (Object.keys(urlToCid).length === 0) return html;

  return html.replace(/<img\b[^>]*>/gi, tag => {
    const srcAttr = tag.match(/\ssrc\s*=\s*["']([^"']*)["']/i);
    const rawSrc = srcAttr?.[1];
    if (!srcAttr || !rawSrc) return tag;

    const decoded = rawSrc.replace(/&amp;/g, '&').replace(/&quot;/g, '"');
    const cid = urlToCid[decoded];
    if (!cid) return tag;

    return tag.replace(srcAttr[0], () => ` src="cid:${cid}"`);
  });
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

export function stripHtmlTags(html: string): string {
  // DOMParser no ejecuta scripts ni handlers (asignar a innerHTML de un div
  // sí dispara onerror de <img>, aunque el div esté desconectado)
  return new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
}

export function getEmailPreview(body: string, maxLength: number = 100): string {
  const plainText = stripHtmlTags(body);
  return truncateText(plainText, maxLength);
}

/**
 * Color de avatar estable por remitente: el mismo correo siempre pinta igual
 * en la lista, el hilo y el panel de lectura.
 */
export function getEmailAvatarColor(email: string | undefined | null): string {
  const colors = ['#0e7490', '#7c3aed', '#b45309', '#be185d', '#161535', '#0f766e', '#64748b', '#1d4ed8'];
  if (!email) return colors[0];
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = email.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

/**
 * Pegado en los editores de correo: si el portapapeles trae HTML (Outlook, Word,
 * Excel) lo inserta ya sanitizado con sanitizeEmailHtml. Devuelve false cuando
 * solo hay texto o imágenes, para dejar el pegado normal del navegador.
 */
export function pasteSanitizedHtml(event: ClipboardEvent): boolean {
  const html = event.clipboardData?.getData('text/html');
  if (!html) return false;
  event.preventDefault();
  document.execCommand('insertHTML', false, sanitizeEmailHtml(html));
  return true;
}
